// Productos del formulario de pedido (pedido de Valentina, 2 oct 2026).
// Un "item" es un producto con N prendas. Desde 2 prendas se pregunta si son iguales (match):
//  · iguales: un color y una personalización para todas, pero cada prenda con su talla (p. ej. M y L);
//  · distintas: cada prenda con su talla, su color y su personalización.
// En la base se guardan como líneas de pedido unidas por "grupo" (iguales agrupadas por talla).
import type { Linea } from './pedidos';

export type Match = '' | 'igual' | 'distinta';
// imagenes: rutas en el depósito "prendas" (dibujos, huellitas o referencias; hasta 3).
export interface Unidad { talla: string; color: string; personalizacion: string; imagenes: string[] }
export interface Item { sku: string; cantidad: number; match: Match; color: string; personalizacion: string; imagenes: string[]; unidades: Unidad[]; precio_unit: number; costo_unit: number }

const unidadVacia = (talla = ''): Unidad => ({ talla, color: '', personalizacion: '', imagenes: [] });
export const itemVacio = (): Item => ({ sku: '', cantidad: 1, match: '', color: '', personalizacion: '', imagenes: [], unidades: [unidadVacia()], precio_unit: 0, costo_unit: 0 });

// Cambia la cantidad: agrega o quita prendas. Con una sola prenda no hay pregunta de match.
export function conCantidad(it: Item, n: number, tallaUnica = ''): Item {
  n = Math.max(0, Math.floor(n) || 0);
  const unidades = Array.from({ length: Math.max(n, 1) }, (_, k) => it.unidades[k] || unidadVacia(tallaUnica));
  if (n <= 1 && it.match === 'distinta') { const u = unidades[0]; return { ...it, cantidad: n, match: '', color: u.color, personalizacion: u.personalizacion, imagenes: u.imagenes, unidades: [{ ...u }] }; }
  return { ...it, cantidad: n, match: n >= 2 ? it.match : '', unidades };
}

// Responder la pregunta de match. Al pasar de iguales a distintas, cada prenda arranca con el color y la personalización
// que tenían todas; al volver a iguales, se toman los de la primera prenda.
export function conMatch(it: Item, match: Match): Item {
  if (match === 'distinta' && it.match !== 'distinta')
    return { ...it, match, unidades: it.unidades.map(u => ({ ...u, color: u.color || it.color, personalizacion: u.personalizacion || it.personalizacion, imagenes: u.imagenes.length ? u.imagenes : it.imagenes })) };
  if (match === 'igual' && it.match === 'distinta')
    return { ...it, match, color: it.color || it.unidades[0].color, personalizacion: it.personalizacion || it.unidades[0].personalizacion, imagenes: it.imagenes.length ? it.imagenes : it.unidades[0].imagenes };
  return { ...it, match };
}

// Item → líneas de pedido. grupo = número del producto en el formulario (1, 2, 3…).
export function lineasDe(items: Item[]): Linea[] {
  return items.flatMap((it, i): Linea[] => {
    const base = { sku: it.sku, precio_unit: it.precio_unit, costo_unit: it.costo_unit, grupo: i + 1 };
    if (it.cantidad >= 2 && it.match === 'distinta')
      return it.unidades.map(u => ({ ...base, talla: u.talla, cantidad: 1, color: u.color, personalizacion: u.personalizacion, imagenes: u.imagenes, iguales: false }));
    // Una prenda, o varias iguales: una línea por talla, en el orden en que se eligieron.
    const porTalla = new Map<string, number>();
    for (const u of it.unidades.slice(0, Math.max(it.cantidad, 1))) porTalla.set(u.talla, (porTalla.get(u.talla) || 0) + 1);
    if (it.cantidad < 1) porTalla.clear();
    return [...porTalla].map(([talla, cantidad]) => ({ ...base, talla, cantidad, color: it.color, personalizacion: it.personalizacion, imagenes: it.imagenes, iguales: it.cantidad >= 2 }));
  });
}

// Líneas guardadas → items del formulario. Las líneas sin grupo (o de un grupo de una sola prenda) son un item cada una.
export function itemsDe(lineas: Linea[]): Item[] {
  const grupos: Linea[][] = [];
  for (const l of lineas) {
    const g = grupos[grupos.length - 1];
    if (g && l.grupo != null && g[0].grupo === l.grupo && g[0].sku === l.sku) g.push(l); else grupos.push([l]);
  }
  return grupos.map(g => {
    const p = g[0], unidades = g.flatMap(l => Array.from({ length: Math.max(l.cantidad, 1) }, () => ({ talla: l.talla, color: l.color, personalizacion: l.personalizacion, imagenes: l.imagenes || [] })));
    const cantidad = unidades.length, iguales = cantidad >= 2 && (g.length === 1 || g.every(l => l.iguales));
    const match: Match = cantidad < 2 ? '' : iguales ? 'igual' : 'distinta';
    return { sku: p.sku, cantidad, match, color: match === 'distinta' ? '' : p.color, personalizacion: match === 'distinta' ? '' : p.personalizacion, imagenes: match === 'distinta' ? [] : p.imagenes || [],
      unidades: match === 'distinta' ? unidades : unidades.map(u => ({ ...u, color: '', personalizacion: '', imagenes: [] })), precio_unit: p.precio_unit, costo_unit: p.costo_unit };
  });
}
